import Backdrop from "@components/Backdrop"
import MenuItem, { ButtonContainer, LinkContainer } from "@components/MenuItem"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import useModalBackgroundClose from "@hooks/useModalBackgroundClose"
import { getIn } from "@utils/index"
import { MobileAndTabletOnly } from "@utils/MediaQuery"
import { getProductURL } from "@utils/urls"
import * as R from "ramda"
import React from "react"
import styled from "styled-components"

import {
  BreadcrumbText,
  DesktopDivider,
  DesktopHeader,
  Headers,
} from "./common"
import SelectBrand from "./SelectBrand"
import SelectDevice from "./SelectDevice"
import SelectProduct from "./SelectProduct"
import {
  getGroupedMenuData,
  getUniqueDevices,
  getUniqueDeviceTypes,
  groupByDevice,
} from "./utils"

const openProps = `
  opacity: 1;
  transform: translateX(0);
  transition: opacity ${animation.quick} ${cubicBezier},
    transform ${animation.medium} ${cubicBezier};
`
const Container = styled.div<{ isOpen?: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: calc(100% - 72px);
  opacity: 0;
  transform: translateX(100%);
  transition:
    opacity ${animation.medium} ${cubicBezier},
    transform ${animation.quick} ${cubicBezier};
  will-change: opacity, transform;
  background-color: ${colors.offWhite};
  cursor: default;
  ${({ isOpen }) => (isOpen ? openProps : "")}

  @media ${breakpoints.tablet} {
    max-width: 64%;
  }

  @media ${breakpoints.laptop} {
    max-width: calc(5 / 12 * 100vw);
  }
`

const DeviceTypes = styled.div`
  overflow: hidden;

  ${ButtonContainer} {
    color: ${colors.black};

    svg {
      stroke: ${colors.black};
    }
  }

  ${LinkContainer} {
    color: ${colors.black};

    svg {
      stroke: ${colors.black};
    }
  }

  @media ${breakpoints.laptop} {
    padding: 0 calc(0.5 / 12 * 100vw - 16px);

    ${ButtonContainer}, ${LinkContainer} {
      height: 64px;
      padding: 0 6px 0 16px;
    }
  }
`

export interface DeviceMenuProductProps {
  brand: {
    name: string
    slug: string
  }
  device: {
    name: string
    slug: string
    type: {
      name: string
    }
  }
  productName: string
  productSlug: string
  series: {
    name: string
    slug: string
  }
  sku?: string
}

interface DeviceMenuProps {
  globalData: any
  isOpen?: boolean
  onRequestClose?: () => void
  products: Array<DeviceMenuProductProps>
}

const DeviceMenu: React.FC<DeviceMenuProps> = ({
  globalData,
  isOpen,
  onRequestClose,
  products = [],
}) => {
  const menuRef: any = React.useRef(null)

  useModalBackgroundClose({
    isOpen,
    modalRef: menuRef,
    onRequestClose,
  })

  const selectedInitialState = {
    brand: undefined,
    device: undefined,
    deviceType: undefined,
  }
  const [selected, setSelected] = React.useState<{
    brand: string | undefined
    device: string | undefined
    deviceType: string | undefined
  }>(selectedInitialState)

  const clearAll = () => {
    setSelected(selectedInitialState)
  }

  const selectDeviceType = (deviceTypeSlug: string) => {
    setSelected({
      brand: undefined,
      device: undefined,
      deviceType: deviceTypeSlug,
    })
  }

  const clearDeviceType = () => {
    setSelected(selectedInitialState)
  }

  const selectBrand = (brandSlug: string) => {
    setSelected({
      brand: brandSlug,
      device: undefined,
      deviceType: selected.deviceType,
    })
  }

  const clearBrand = () => {
    setSelected({
      brand: undefined,
      device: undefined,
      deviceType: selected.deviceType,
    })
  }

  const selectDevice = (deviceSlug: string, brandSlug?: string) => {
    setSelected({
      brand: brandSlug || selected.brand,
      device: deviceSlug,
      deviceType: selected.deviceType,
    })
  }

  const clearDevice = () => {
    setSelected({
      brand: selected.brand,
      device: undefined,
      deviceType: selected.deviceType,
    })
  }

  React.useEffect(() => {
    if (!isOpen) {
      clearAll()
    }
  }, [isOpen])

  const filterFunnel = {
    brand: {},
    deviceType: {},
  }

  const formattedProducts = React.useMemo(() => {
    const [productsWithOneDevice, productsWithMultipleDevices] = R.partition(
      (product: any) => {
        if (product.devices && product.devices.length > 1) {
          return false
        }
        return true
      }
    )(products)

    const adjustedProductNodes = [...productsWithOneDevice]

    if (productsWithMultipleDevices.length > 0) {
      productsWithMultipleDevices.forEach((product) => {
        product.devices.forEach((device) => {
          adjustedProductNodes.push({
            ...product,
            device,
          })
        })
      })
    }

    return adjustedProductNodes
  }, [products])

  const productsGroupedByDevice = React.useMemo(
    () => groupByDevice(formattedProducts),
    [formattedProducts]
  )

  const uniqueDevices = React.useMemo(
    () => getUniqueDevices(formattedProducts),
    [formattedProducts]
  )

  const uniqueDeviceTypes = React.useMemo(
    () => getUniqueDeviceTypes(uniqueDevices),
    [uniqueDevices]
  )

  const formatMenuData = () => {
    const groupedMenuData: any = getGroupedMenuData(uniqueDevices)

    return globalData.menuItems.reduce((acc, { menuLabel, slug }) => {
      const deviceTypeData: any = {
        deviceType: {
          label: menuLabel,
          slug,
        },
      }

      const thisBrandOrder = R.find(
        ({ deviceType }) => deviceType.slug === slug
      )(globalData.menuBrandOrder) as any

      const brandOrder = getIn(thisBrandOrder, "brandOrder", [])

      const brands = R.sort((a, b) => {
        const indexA = R.findIndex(({ slug }) => slug === a)(brandOrder)
        const indexB = R.findIndex(({ slug }) => slug === b)(brandOrder)
        if (indexA === -1 && indexB !== -1) {
          return 1
        } else if (indexA !== -1 && indexB === -1) {
          return -1
        } else {
          return indexA - indexB
        }
      }, R.keys(groupedMenuData[slug]))

      deviceTypeData.brandsToShow = getIn(thisBrandOrder, "brandsToShow")
      deviceTypeData.brands = brands.map((brand) => {
        const devices = groupedMenuData[slug][brand]

        const brandData: any = {
          brandName: devices[0].brand.name,
          brandSlug: devices[0].brand.slug,
        }

        if (devices.length === 1) {
          if (filterFunnel.brand[slug]) {
            filterFunnel.brand[slug][brandData.brandSlug] = devices[0].device
          } else {
            filterFunnel.brand[slug] = {
              [brandData.brandSlug]: devices[0].device,
            }
          }
        }

        const thisDeviceOrder: any = R.find(
          ({ brand, deviceType }) =>
            brand.slug === brandData.brandSlug && deviceType.slug === slug
        )(globalData.menuDeviceOrder)

        const deviceOrder = getIn(thisDeviceOrder, "deviceOrder", [])

        brandData.devicesToShow = getIn(thisDeviceOrder, "devicesToShow")
        brandData.devices = R.pipe(
          R.sort((a: any, b: any) => {
            const indexA = R.findIndex(({ slug }) => slug === a.device.slug)(
              deviceOrder
            )
            const indexB = R.findIndex(({ slug }) => slug === b.device.slug)(
              deviceOrder
            )
            if (indexA === -1 && indexB !== -1) {
              return 1
            } else if (indexA !== -1 && indexB === -1) {
              return -1
            } else {
              return indexA - indexB
            }
          }),
          R.map(({ device }) => {
            const products = productsGroupedByDevice[device.slug]
            return {
              deviceName: device.name,
              deviceSlug: device.slug,
              products,
            }
          })
        )(devices)

        return brandData
      })

      if (deviceTypeData.brands.length === 1) {
        if (filterFunnel.deviceType[slug]) {
          filterFunnel.deviceType[slug][deviceTypeData.brands[0].brandSlug] =
            deviceTypeData.brands[0]
        } else {
          filterFunnel.deviceType[slug] = {
            [deviceTypeData.brands[0].brandSlug]: deviceTypeData.brands[0],
          }
        }
      }

      return {
        ...acc,
        [slug]: deviceTypeData,
      }
    }, {})
  }

  const formatDeviceType = ({ menuLabel, slug }) => {
    const thisFilterFunnel: any = getIn(filterFunnel, `deviceType.${slug}`, {})
    const brands = Object.keys(thisFilterFunnel)
    const brand = brands.length > 0 && thisFilterFunnel[brands[0]]

    if (
      brand &&
      brand.devices.length === 1 &&
      brand.devices[0].products.length === 1
    ) {
      const shopURL = getProductURL(brand.devices[0].products[0].productSlug)
      if (
        typeof window !== "undefined" &&
        getIn(window, "location.pathname") !== shopURL
      ) {
        return {
          label: menuLabel,
          link: shopURL,
        }
      } else {
        return {
          label: menuLabel,
          onClick: onRequestClose,
        }
      }
    } else if (brand && brand.devices.length === 1) {
      return {
        label: menuLabel,
        onClick: () => {
          setSelected({
            brand: brand.brandSlug,
            device: brand.devices[0].deviceSlug,
            deviceType: slug,
          })
        },
      }
    } else if (brand) {
      return {
        label: menuLabel,
        onClick: () => {
          setSelected({
            brand: brand.brandSlug,
            device: undefined,
            deviceType: slug,
          })
        },
      }
    } else {
      return {
        label: menuLabel,
        onClick: () => {
          selectDeviceType(slug)
        },
      }
    }
  }

  const menuData = React.useMemo(
    () => formatMenuData(),
    [globalData, uniqueDevices]
  )

  const formattedDeviceTypes = React.useMemo(() => {
    const menuItems = getIn(globalData, "menuItems", [])
    return menuItems
      .filter(({ slug }) => {
        return (
          R.findIndex(
            ({ deviceType }: any) => deviceType.slug === slug,
            uniqueDeviceTypes
          ) !== -1
        )
      })
      .map(formatDeviceType)
  }, [globalData, uniqueDeviceTypes, menuData])

  return (
    <Backdrop isOpen={isOpen}>
      <Container isOpen={isOpen} ref={menuRef}>
        <Headers>
          <MobileAndTabletOnly>
            <BreadcrumbText>{`All ${getIn(
              products,
              "0.series.name"
            )} Products`}</BreadcrumbText>
          </MobileAndTabletOnly>
        </Headers>
        <DesktopHeader>{`All ${getIn(
          products,
          "0.series.name"
        )} Products`}</DesktopHeader>
        <DeviceTypes>
          <DesktopDivider />
          {formattedDeviceTypes.map((props) => (
            <MenuItem key={props.label} {...props} />
          ))}
        </DeviceTypes>
        <SelectBrand
          data={menuData}
          deviceTypeLabel={getIn(
            menuData,
            `${selected.deviceType}.deviceType.label`
          )}
          deviceTypeSlug={selected.deviceType}
          filterFunnel={filterFunnel}
          onRequestClose={clearDeviceType}
          onSelectBrand={selectBrand}
          onSelectDevice={selectDevice}
        />
        <SelectDevice
          brandSlug={selected.brand}
          data={menuData}
          deviceTypeLabel={getIn(
            menuData,
            `${selected.deviceType}.deviceType.label`
          )}
          deviceTypeSlug={selected.deviceType}
          onRequestClose={clearBrand}
          onSelectDevice={selectDevice}
        />
        <SelectProduct
          brandSlug={selected.brand}
          data={menuData}
          deviceSlug={selected.device}
          deviceTypeSlug={selected.deviceType}
          onRequestClose={clearDevice}
        />
      </Container>
    </Backdrop>
  )
}

export default DeviceMenu
